import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import LoyaltyService from "services/LoyaltyService";
import unpackAndShowErrors from "helpers/djangoErrorUnapacker";
import { FIRST_PAGE } from "constants/CommonConstants";
import { infiniteQueryConfig } from "queries/utils";
import LoyaltyUsersService from "services/LoyaltyUsersService";

const GET_LEVEL_MEMBERS = "GET_LEVEL_MEMBERS";
const GET_SINGLE_USER_REWARDS = "GET_SINGLE_USER_REWARDS";
const GET_REWARDS = "GET_REWARDS";
const GET_USER_LEVEL = "GET_USER_LEVEL";
const GET_NEXT_APPOINTMENT = "GET_NEXT_APPOINTMENT";
const GET_USER_ACTIVITY = "GET_USER_ACTIVITY";
const GET_USER_MEMBERSHIP = "GET_USER_MEMBERSHIP";
const GET_LOYALTY_CONTACT = "GET_LOYALTY_CONTACT";
const GET_DOCTORS = "GET_DOCTORS";
const GET_LOYALTY_ITEM_OFFER = "GET_LOYALTY_ITEM_OFFER";
const GET_CONSENT_FORMS = "GET_CONSENT_FORMS";
const GET_USER_POINT_HISTORY = "GET_USER_POINT_HISTORY";
const GET_LOYALTY_USER_VOUCHERS = "GET_LOYALTY_USER_VOUCHERS";

const useGetLevelMembers = (levelId) =>
  useInfiniteQuery({
    queryKey: [GET_LEVEL_MEMBERS, levelId],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      LoyaltyService.getLevelMembers(levelId, { page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.next) {
        return lastPage.data.current + 1;
      }
      return undefined;
    },
    initialPageParam: FIRST_PAGE,
    enabled: !!levelId,
  });

const useGetUserLoyaltyProfile = () => {
  return useQuery({
    queryKey: [GET_USER_LEVEL],
    queryFn: () => LoyaltyService.getUserLoyaltyProfile(),
  });
};

const useRedeemReward = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => LoyaltyService.redeemReward(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_REWARDS]);
      onSuccess();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useGetNextAppointment = () => {
  return useQuery({
    queryKey: [GET_NEXT_APPOINTMENT],
    queryFn: () => LoyaltyService.getNextAppointment(),
  });
};

const useGetUserActivity = (payload) => {
  return useQuery({
    queryKey: [GET_USER_ACTIVITY, payload],
    queryFn: () => LoyaltyService.getUserActivity(payload),
  });
};

const useGetSingleUserRewards = (payload) => {
  return useQuery({
    queryKey: [GET_SINGLE_USER_REWARDS, payload],
    queryFn: () => LoyaltyService.getSingleUserRewards(payload),
  });
};

const useGetSingleUserRewardsInfinite = (payload) =>
  useInfiniteQuery({
    queryKey: [GET_SINGLE_USER_REWARDS, "infinite", payload],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      LoyaltyService.getSingleUserRewards({ ...payload, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.next) {
        return lastPage.data.current + 1;
      }
      return undefined;
    },
    initialPageParam: FIRST_PAGE,
  });

const useGetUserMembership = () => {
  return useQuery({
    queryKey: [GET_USER_MEMBERSHIP],
    queryFn: () => LoyaltyService.getUserMembership(),
  });
};

const useGetLoyaltyContact = (enabled = true) => {
  return useQuery({
    queryKey: [GET_LOYALTY_CONTACT],
    queryFn: () => LoyaltyService.getLoyaltyContact(),
    enabled,
  });
};

const useGetDoctors = (payload) =>
  useInfiniteQuery({
    queryKey: [GET_DOCTORS, payload],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      LoyaltyService.getDoctors({
        ...payload,
        page: pageParam,
      }),
    ...infiniteQueryConfig,
  });

const useGetLoyaltyItemOffer = (payload) =>
  useInfiniteQuery({
    queryKey: [GET_LOYALTY_ITEM_OFFER, payload],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      LoyaltyService.getLoyaltyItemOffer({
        ...payload,
        page: pageParam,
      }),
    ...infiniteQueryConfig,
  });

const useGetConsentForms = (payload, isEnabled = true) =>
  useQuery({
    queryKey: [GET_CONSENT_FORMS, payload],
    queryFn: () => LoyaltyService.getConsentForms(payload),
    enabled: isEnabled,
  });

const useGetConsentFormsInfinite = (payload) =>
  useInfiniteQuery({
    queryKey: [GET_CONSENT_FORMS, "infinite", payload],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      LoyaltyService.getConsentForms({ ...payload, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.next) {
        return lastPage.data.current + 1;
      }
      return undefined;
    },
    initialPageParam: FIRST_PAGE,
    cacheTime: 0,
  });

const useSignConsentFormByPatientAction = (handleSuccess, handleError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) =>
      LoyaltyService.signConsentFormByPatient(id, payload),
    onSuccess: () => {
      handleSuccess && handleSuccess();
      queryClient.invalidateQueries([GET_CONSENT_FORMS]);
    },
    onError: () => {
      handleError && handleError();
    },
  });
};

const useAddContactToLoyalty = (onSuccess) => {
  return useMutation({
    mutationFn: (payload) => LoyaltyUsersService.createLoyaltyUser(payload),
    onSuccess: () => {
      onSuccess && onSuccess();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useGetUserPointHistory = (payload) => {
  return useQuery({
    queryKey: [GET_USER_POINT_HISTORY, payload],
    queryFn: () => LoyaltyUsersService.getUserPointsHistory(payload),
    enabled: !!payload?.loyalty_user,
  });
};

const useGetLoyaltyUserVouchers = (payload) => {
  return useQuery({
    queryKey: [GET_LOYALTY_USER_VOUCHERS, payload],
    queryFn: () => LoyaltyUsersService.getLoyaltyUserVouchers(payload),
    enabled: !!payload?.loyalty_user,
  });
};

export {
  GET_LEVEL_MEMBERS,
  GET_REWARDS,
  GET_USER_LEVEL,
  GET_NEXT_APPOINTMENT,
  GET_USER_ACTIVITY,
  GET_USER_MEMBERSHIP,
  GET_LOYALTY_CONTACT,
  GET_LOYALTY_ITEM_OFFER,
  GET_USER_POINT_HISTORY,
  GET_LOYALTY_USER_VOUCHERS,
  useGetLevelMembers,
  useRedeemReward,
  useGetUserLoyaltyProfile,
  useGetSingleUserRewards,
  useGetSingleUserRewardsInfinite,
  useGetNextAppointment,
  useGetUserActivity,
  useGetUserMembership,
  useGetLoyaltyContact,
  useGetDoctors,
  useGetLoyaltyItemOffer,
  useGetConsentForms,
  useGetConsentFormsInfinite,
  useSignConsentFormByPatientAction,
  useAddContactToLoyalty,
  useGetUserPointHistory,
  useGetLoyaltyUserVouchers,
};
